import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate, useRouteContext } from "@tanstack/react-router";
import { useState, type JSX } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { graphql } from "@repo/graphql-types";
import Left from "@repo/icons/default/Left";
import { CpsDialog, CpsInput, CpsSpinner } from "corpus";

import {
  FormControl,
  FormField,
  FormHandlerSubmit,
  FormItem,
  FormRoot,
  FormSubmitButton,
} from "@/components/ui/form/form";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { useGraphQLMutation } from "@/hooks/use-graphql";
import { checkDomainAvailability } from "@/lib/brasil-api-registro-br";
import { getGraphQLErrorCode } from "@/lib/graphql-error-code";

const RegisterDomainMutation = graphql(`
  mutation RegisterDomainMutation($input: LivanceApiRegistraDominioInput!) {
    LivanceApiRegistraDominio(arg1: $input) {
      message
      success
    }
  }
`);

// RFC 1123 compliant validation
const rfc1123Regex = /^(?!-)(?:[a-z0-9-]{0,62}[a-z0-9])?(?:\.[a-z]{2,63}){1,63}$/;

const customWebsiteFormSchema = z.object({
  domainName: z
    .string()
    .min(1, "Campo obrigatório")
    .transform((data) => data.trim())
    .refine((data) => data.endsWith(".br"), "O domínio deve terminar com .br")
    .refine(
      (data) => !data.toLowerCase().startsWith("www."),
      "O domínio não pode ter o prefixo www",
    )
    .refine((data) => {
      const parts = data.split(".");
      return parts.length === 3 && parts.every((part) => part.length > 0);
    }, "O domínio não pode conter subdomínios. Utilize o formato: nomedosite.com.br")
    .refine(
      (data) => rfc1123Regex.test(data),
      "Domínio pode conter apenas letras minúsculas, números e hífens, sem iniciar ou terminar com hífens",
    )
    .refine(async (data) => {
      if (!data || data.trim() === "") {
        return false;
      }

      const { statusCode } = await checkDomainAvailability(data);
      return statusCode === 0;
    }, "O domínio não está disponível"),
});

export type CustomWebsiteFormValues = z.infer<typeof customWebsiteFormSchema>;

const CustomWebsiteFormPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { user } = useRouteContext({ from: "__root__" });
  const { mutateAsync } = useGraphQLMutation(RegisterDomainMutation);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<CustomWebsiteFormValues>({
    resolver: zodResolver(customWebsiteFormSchema),
    defaultValues: {
      domainName: "",
    },
  });

  const domainName = form.watch("domainName").trim();

  const onSubmit = (): void => {
    setShowConfirmDialog(true);
  };

  const onConfirm = async (): Promise<void> => {
    setIsLoading(true);

    try {
      const data = await mutateAsync({
        input: {
          codUsuario: user.codUsuario,
          dominio: domainName,
        },
      });

      const path = data.LivanceApiRegistraDominio?.success
        ? "/custom-website/confirmation"
        : "/custom-website/confirmation/error";

      await navigate({ to: path, search: { dominio: domainName } });
    } catch (error) {
      const errorCode = getGraphQLErrorCode(error);

      await navigate({
        to: "/custom-website/confirmation/error",
        search: { errorCode },
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Site personalizado" align="center" />
      </HeaderRoot>
      <Page>
        {isLoading ? (
          <div className="fixed flex inset-0 content-center justify-center bg-black/30 z-50">
            <CpsSpinner />
          </div>
        ) : null}

        <CpsDialog
          btnOnClick={() => {
            void onConfirm();
          }}
          description={`Você tem certeza que deseja utilizar esse domínio? "${domainName}" será cadastrado para você pelo período de 1 ano e não poderá ser alterado.`}
          primaryBtnLabel="Confirmar"
          setShow={setShowConfirmDialog}
          show={showConfirmDialog}
          title="Confirmar Cadastro"
        />
        <FormRoot {...form}>
          <FormHandlerSubmit handleSubmit={onSubmit}>
            <div className="mb-4">
              <p className="font-medium text-neutral-800 mb-2">
                Preencha com o nome de site que deseja cadastrar
              </p>
              <p className="text-sm text-neutral-600">
                O domínio é o endereço que usarão para encontrar você online. Escolha um
                nome profissional, fácil de lembrar e que represente quem você é.
              </p>
            </div>
            <FormField
              control={form.control}
              name="domainName"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <CpsInput
                      title="Nome do domínio"
                      placeholder="exemplo.com.br"
                      inputMode="text"
                      type="text"
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormSubmitButton
              type="submit"
              disabled={!domainName}
              loading={form.formState.isSubmitting}
            >
              Cadastrar Domínio
            </FormSubmitButton>
          </FormHandlerSubmit>
        </FormRoot>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/custom-website/form/")({
  component: CustomWebsiteFormPage,
  validateSearch: z.object({
    action: z.string().optional(),
  }),
});
